import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import {
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    InputAdornment,
    TextField,
    Paper,
    IconButton,
    Tooltip,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    Alert,
} from '@mui/material';
import Skeleton from '@mui/material/Skeleton';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';

import {
    LoadingBar,
    PrimaryButton,
    FilterList,
    CircularLoading,
    OnOffSwitch,
} from "../../Common";

import Routes from "../../../routes";
import UserInfo from "../../../UserInfo";
import InstanceAssociationsTable from "./InstanceAssociationsTable";
import { trakrefBlue } from "../../../color_utils";
import "./index.css";
import Enums from "../../../enums";

const initialState = {
    regions: [],
    savedRegions: [],
    expandedRegionId: "",
    searchTerm: "",
    enabledRegionIds: [],
    assignedLocationIds: [],
    regionIdsAllLocationsAssigned: [],
    locationPermissionAccessToAll: "0",
    noData: false,
    isFirstRender: true,
    isProviderRestricted: false,
};

const filterListOptions = [
    { type: "text", name: "Location Name", property: "locationName", translateId: "Location.Name" },
    { type: "text", name: "Location Zip Code", property: "physicalZip", translateId: "Profile.ZipCode" },
    { type: "dropdown", name: "Location Type", property: "locationType", translateId: "Location.Type" },
    { type: "dropdown", name: "Country", property: "country", translateId: "Location.Country" },
    { type: "dropdown", name: "State", property: "state", translateId: "Location.State" },
    { type: "text", name: "City", property: "city", translateId: "Location.City" },
];

const columns = [
    { id: 'LocationName', title: "Location Name" },
    { id: 'LocationCode', title: "Location Code" },
    { id: 'LocationType', title: "Location Type" },
    { id: 'ActiveSystemCount', title: "Active System Count" },
    { id: 'PhysicalAddress1', title: "Address" },
    { id: 'PhysicalState1', title: "State" },
];

const gridContainerProps = { spacing: 2, columns: 8, direction: "row" };

const AssignedLocations = ({
    comingFromUserForm = false,
    userInformation: {
        id: userId,
        canEditLocation = null,
        restrictedLocations: userRestrictedLocations = null,
        isSystemAdmin,
        isSuperTenantAdmin,
        isTenantAdmin,
        isRegionalAdmin,
        isAPIFacilityManager,
        userType,
        userStatus,
        userInstanceAssociationIds
    },
    // userInformation,
    isEditing = false,
    setFormState = () => { },
    navigate,
    linkServiceProviders = false,
    gettingServicerLocations = false,
    isOptionDisabled = false,
    servicerInstanceId = 0,
    setLocationPermissionAccessToAll = null,
}) => {
    const dynamicWidth = comingFromUserForm ? "100%" : "80%";
    const isCreatingNewUser = !isEditing && comingFromUserForm;
    // user is viewing (not editing)
    const isViewingAssignedLocations = !comingFromUserForm && !linkServiceProviders;
    const addingNewRegionalAdmin = () => {
        return !!(isCreatingNewUser && userType === Enums.UserTypes.RegionalAdministrator && userInstanceAssociationIds.length);
    }

    const nameMode = isEditing ? "editMode" : "createMode";
    const pageKey = "AssignedLocations";
    const filterKey = `${pageKey}/${navigate.pageName}/${nameMode}filters${UserInfo.instanceId}`;
    const savedFilters = sessionStorage.getItem(filterKey);
    const parsedFilters = savedFilters ? JSON.parse(savedFilters) : [];
    const [loading, setLoading] = useState(true);
    const [state, setAllState] = useState(initialState);
    const [filterState, setNewFilterState] = useState({
        tableURL: `${pageKey}/${navigate.pageName}/${nameMode}`,
        filterListOptions,
        filterValues: [],
        filters: parsedFilters,
        appliedFilters: [],
        loading: false,
        selectedFilter: null,
        clearFilters: !!savedFilters,
    });
    const renderRegions = state.locationPermissionAccessToAll === "0";

    const setState = (new_state_obj) => (
        setAllState((prevState) => (
            { ...prevState, ...new_state_obj }
        ))
    );

    const setFilterState = (new_filter_state_obj) => (
        setNewFilterState((prevFilterState) => (
            { ...prevFilterState, ...new_filter_state_obj }
        ))
    );

    const closeRegionDetails = () => setState({ expandedRegionId: "" });

    const handleRegionOpen = (regionId) => {
        const regionAlreadyOpen = regionId === state.expandedRegionId;
        if (regionAlreadyOpen) {
            return closeRegionDetails();
        }
        return setState({ expandedRegionId: regionId });
    };

    const handleRegionToggle = (regionId) => {
        let newEnabledRegionIds = [...state.enabledRegionIds];

        // Unchecking region
        if (state.enabledRegionIds.includes(regionId)) {
            newEnabledRegionIds = newEnabledRegionIds.filter(id => id !== regionId);
            const regionLocationIds = state.regions.find(r => r.id === regionId).locations.map(l => l.id);
            const filteredLocationIds = [...state.assignedLocationIds].filter(id => !regionLocationIds.includes(id));
            const regionIdsAllLocationsAssigned = [...state.regionIdsAllLocationsAssigned].filter(id => id !== regionId);

            setState({
                assignedLocationIds: filteredLocationIds,
                regionIdsAllLocationsAssigned
            });
            // set state "assignedLocationIds" from parent component "UserForm.js"
            setFormState(prev => ({ ...prev, assignedLocationIds: filteredLocationIds }));
        } else {
            newEnabledRegionIds.push(regionId);
            handleLocationCheck({ regionId, allLocationsChecked: true });
        }
        return setState({ enabledRegionIds: newEnabledRegionIds });
    };

    const handleClearFilters = (e) => {
        e.stopPropagation();
        sessionStorage.removeItem(`${filterState.tableURL}filters${UserInfo.instanceId}`);
        return setFilterState({
            loading: true,
            filters: [],
            clearFilters: true,
        });
    };

    const searchLocations = (locations, searchTerm) => (
        locations.filter(l => l.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const handleSearch = (searchTerm, regionId) => {
        setState({ searchTerm });
        const { savedRegions } = state;
        let regions = [...savedRegions];
        const regionIndex = savedRegions.findIndex(r => r.id === regionId);
        const searchRegionLocation = savedRegions.find(r => r.id === regionId);
        let filteredDropDownRegion = { ...searchRegionLocation };
        const { locations } = searchRegionLocation;
        const searchResults = searchLocations(locations, searchTerm);
        console.log('searchResults', searchResults);
        filteredDropDownRegion.locations = searchResults;
        regions[regionIndex] = filteredDropDownRegion;

        return setState({ regions });
    };

    const handleLocationCheck = ({
        regionId = null,
        checkedLocationId,
        allLocationsChecked = false,
    }) => {
        const region = state.regions.find(r => r.id === regionId);
        const regionLocationIds = region.locations.map(l => l.id);
        let newAssignedLocationIds = [...state.assignedLocationIds];
        let regionIdsAllLocationsAssigned = [...state.regionIdsAllLocationsAssigned];
        let assignedLocationIds = region.locations.map(l => l.id).concat(state.assignedLocationIds);

        // All locations checked or unchecked
        if (allLocationsChecked) {

            if (!regionIdsAllLocationsAssigned.includes(region.id)) {
                regionIdsAllLocationsAssigned.push(region.id);
                newAssignedLocationIds = newAssignedLocationIds.concat(regionLocationIds);
            } else {
                regionIdsAllLocationsAssigned = regionIdsAllLocationsAssigned.filter(id => id !== region.id);
                assignedLocationIds = assignedLocationIds.filter(id => !regionLocationIds.includes(id));
                newAssignedLocationIds = newAssignedLocationIds.filter(id => !regionLocationIds.includes(id));
            }

            // set assignedLocationIds using hook function from parent component "UserForm.js"
            setFormState(prev => ({ ...prev, assignedLocationIds: newAssignedLocationIds.sort((a, b) => a - b) }));

            return setState({ assignedLocationIds, regionIdsAllLocationsAssigned });
        }

        // Single location checked or unchecked 
        if (state.assignedLocationIds.includes(checkedLocationId)) {
            newAssignedLocationIds = newAssignedLocationIds.filter(id => id !== checkedLocationId);
            if (regionIdsAllLocationsAssigned.includes(region.id)) {
                regionIdsAllLocationsAssigned = regionIdsAllLocationsAssigned.filter(id => id !== region.id);
            }
        } else {
            newAssignedLocationIds.push(checkedLocationId);
        }

        // set assignedLocationIds using hook function from parent
        setFormState(prev => ({ ...prev, assignedLocationIds: newAssignedLocationIds.sort((a, b) => a - b) }));

        return setState({
            assignedLocationIds: newAssignedLocationIds,
            regionIdsAllLocationsAssigned
        });
    };

    const handleAccessToAllChange = (newAccessToAllOption) => {
        let restrictedLocations = true;

        // Access to all Locations option
        if (newAccessToAllOption === "1") {
            restrictedLocations = false;
        }

        // set formState using hook function from parent component "UserForm.js"
        setFormState(prev => ({ ...prev, restrictedLocations }));
        setState({ locationPermissionAccessToAll: newAccessToAllOption });
    };

    // only show regions that have at least 1 location
    const getfilteredRegions = regions => regions.filter(r => r.locations && r.locations.length > 0);

    // create parent for all locations with no associated region
    const createParentLocationsWithNoRegion = (locationsNoRegions) => {
        if (locationsNoRegions.length === 0) {
            return [];
        }
        return [{
            id: 0,
            label: "Locations With No Associated Region",
            instanceId: 248,
            locations: locationsNoRegions,
            numberOfLocations: locationsNoRegions.length
        }];
    };

    const createAllRegions = (regions, locationsWithNoRegion) => {
        const allRegions = [
            ...createParentLocationsWithNoRegion(locationsWithNoRegion),
            ...getfilteredRegions(regions)
        ];

        if (linkServiceProviders) {
            return allRegions;
        }

        // only show regions with at least 1 assigned location

        if (!isViewingAssignedLocations && isCreatingNewUser && userType === Enums.UserTypes.RegionalAdministrator && userInstanceAssociationIds.length) {
            const allRegionIds = allRegions.filter(r => userInstanceAssociationIds.map(id => id).includes(r.id));
            const allLocationIds = allRegionIds.map(r => r.locations).flat().map(l => l.id);
            
            setFormState(prev => ({ ...prev, assignedLocationIds: allLocationIds }));
            setState({
                assignedLocationIds: allLocationIds
            });

            return allRegionIds;
        }

        if (isViewingAssignedLocations) {
            // unique regionIds with at least 1 assigned location
            const regionIdsWithAtMin1AssignedLocation = [...new Set(
                allRegions
                    .map(r => r.locations)
                    .flat()
                    .filter(l => l.isAssigned)
                    .map(l => l.instanceAssociationId)
            )];

            // only show assigned locations within enabled regions
            return allRegions
                .filter(r => regionIdsWithAtMin1AssignedLocation.includes(r.id))
                .map((region) => {
                    const locations = region.locations.filter(l => l.isAssigned);
                    return {
                        ...region,
                        locations,
                        numberOfLocations: locations.length
                    };
                });
        }

        return allRegions;
    };

    const determineTooltipText = (regionId) => {
        if (isViewingAssignedLocations || addingNewRegionalAdmin()) return "";

        if (state.enabledRegionIds.includes(regionId)) {
            return "Disable Region";
        }
        return "Enable Region";
    };

    const determineRadioSelectionDisabled = () => {
        // User is viewing AssignedLocations via Link Service Providers page

        if (linkServiceProviders) {
            return false;
        }

        if (isViewingAssignedLocations) {
            return true;
        }

        // user cannot edit their own assigned locations
        if (userId === UserInfo.userId) {
            return true;
        }

        // these user types should have access to all locations - disable assigning locations
        if (
            isSystemAdmin
            || isSuperTenantAdmin
            || isTenantAdmin
            || isRegionalAdmin
            || isAPIFacilityManager
        ) {
            return true;
        }

        // Tech managers cannot assign locations to other tech managers
        if (UserInfo.isTechnicianManager() && userType === Enums.UserTypes.TechnicianManager) {
            return true;
        }

        // creating new user - these user types should have access to all locations
        if (
            isCreatingNewUser
            && [
                Enums.UserTypes.SystemAdministrator,
                Enums.UserTypes.SuperTenantAdmin,
                Enums.UserTypes.TenantAdministrator,
                Enums.UserTypes.APIFacilityManager,
                Enums.UserTypes.RegionalAdministrator
            ].includes(userType)
        ) {
            return true;
        }

        return false;
    };

    const RegionLabel = ({ regionName, numberOfLocations }) => (
        filterState.loading ? (
            <>
                {`${regionName}`} <CircularLoading size={15} marginLeft={1} />
            </>
        ) : (
            <>
                {`${regionName} (${numberOfLocations})`}
            </>
        )
    );

    const NoDataMessage = () => {
        if (filterState.loading)
            return <>Resetting Results...</>

        if (filterState.filters.length > 0) {
            return <><span style={{ fontSize: "20px", fontWeight: "bold" }}>Oops... It is empty here...</span> <br /> Please add a location or change your search criteria.</>
        }

        return <><span style={{ fontSize: "20px", fontWeight: "bold" }}>Oops... It is empty here...</span> <br /> Please add a location to get started.</>
    }

    const NoData = ({ usePaperBackground = true }) => (
        <Grid container component={usePaperBackground ? Paper : 'span'}>
            <Grid item xs={12} align='center'>
                <img
                    style={{ paddingTop: '4%' }}
                    src={filterState.filters.length > 0 ? "/images/ufo_placeholder.svg" : "/images/no_report_selected.svg"}
                    alt='No Data'
                />
            </Grid>
            <Grid item xs={12}>
                <Typography
                    variant='h5'
                    align='center'
                    style={{
                        fontSize: '14px',
                        marginTop: '2%',
                        paddingBottom: '2%',
                        color: '#1A173B'
                    }}
                    
                >
                    <NoDataMessage />
                </Typography>
            </Grid>
            {filterState.loading && (
                <Grid item xs={12} sx={{ paddingBottom: '20px' }}>
                    <LoadingBar paddingTop={0} />
                </Grid>
            )}
        </Grid>
    );

    const LoadingSkeleton = () => (
        <Grid
            container
            {...gridContainerProps}
            sx={{ width: dynamicWidth, marginTop: '2%', marginLeft: '1%' }}
        >
            <Grid item xs={4} alignSelf="center">
                <Skeleton
                    variant="rectangular" width={87} height={22}
                />
            </Grid>
            {isViewingAssignedLocations || addingNewRegionalAdmin() && (
                <Grid item sx={{ marginLeft: "auto", marginBottom: "1%" }} align="right">
                    <Skeleton
                        variant="rectangular" width={186} height={37}
                    />
                </Grid>
            )}
            {Array(10).fill(null).map(_ => (
                <Grid item xs={8}>
                    <Skeleton
                        variant="rectangular" width="100%" height={54}
                    />
                </Grid>
            ))}
        </Grid>
    );

    const fetchInstanceAssociationData = async () => {
        // Get assigned locations either based on servicer or user
        const URL = linkServiceProviders
            ? Routes.Instance.GetServicerInstanceAssociationsWithLocations(servicerInstanceId)
            : Routes.Instance.GetByInstanceIdWithLocations(userId);

        const response = await fetch(
            URL,
            {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    filters: filterState.filters
                })
            }
        );

        return response;
    };

    const fetchAllData = useCallback(async () => {
        await Promise.all([
            fetchInstanceAssociationData(),
            fetch(Routes.Location.GetInstanceAssociationsWithLocationsFilterValues())
        ]).then(async ([regionsAndLocationsResponse, rawFilterValues]) => {
            const data = await regionsAndLocationsResponse.json();
            const filterValues = await rawFilterValues.json();
            const { regions, locationsWithNoRegions, isProviderRestricted = false } = data;

            const allRegions = createAllRegions(regions, locationsWithNoRegions);

            // only get locationIds that are assigned if editing existing user
            const assignedLocations = allRegions.map(r => r.locations).flat().filter(l => l.isAssigned);
            let locationPermissionAccessToAll = userRestrictedLocations || assignedLocations.length > 0 ? "0" : "1";

            // if creating new user, assign all locations by default
            if (isCreatingNewUser) {
                locationPermissionAccessToAll = userType === Enums.UserTypes.RegionalAdministrator ? "0" : "1";
            }

            const assignedLocationIds = assignedLocations.map(l => l.id);
            const enabledRegionIds = assignedLocations.map(l => l.instanceAssociationId);
            

            // Default restrictedLocations based on locationPermissionAccessToAll
            // "1" = access to all (not restricted), "0" = specific locations (restricted)
            const restrictedLocations = locationPermissionAccessToAll === "0";

            if (!isViewingAssignedLocations && isCreatingNewUser && userType === Enums.UserTypes.RegionalAdministrator && userInstanceAssociationIds.length) {
                setState({
                    regions: allRegions,
                    savedRegions: allRegions,
                    enabledRegionIds: userInstanceAssociationIds,
                    locationPermissionAccessToAll,
                    isFirstRender: false,
                    isProviderRestricted,
                });
                setFormState(prev => ({ ...prev, restrictedLocations }));
            }
            else
            {
                setState({
                    regions: allRegions,
                    savedRegions: allRegions,
                    assignedLocationIds,
                    enabledRegionIds,
                    locationPermissionAccessToAll,
                    isFirstRender: false,
                    isProviderRestricted,
                });

                // Set parent state on load. The LinkServiceProviders form owns
                // restrictedLocations via route state, so don't overwrite it here —
                // a late-resolving fetch would otherwise revert a user's toggle.
                setFormState(prev => ({
                    ...prev,
                    ...(linkServiceProviders ? {} : { restrictedLocations }),
                    assignedLocationIds: assignedLocationIds.sort((a, b) => a - b)
                }));
            }

            setFilterState({ filterValues });
            setLoading(false);
        })
            .catch((err) => {
                toast.error("Error loading Regions / Divisions!", { duration: 5000 });
                console.log('Error', err);
            });
    }, [userType, userInstanceAssociationIds]);

    useEffect(() => {
        if (!state.isFirstRender) {
            setState({ expandedRegionId: '' });
            setFilterState({ loading: true });

            const fetchData = async () => {
                const response = await fetchInstanceAssociationData();
                const data = await response.json();
                const { regions, locationsWithNoRegions } = data;
                const allRegions = createAllRegions(regions, locationsWithNoRegions);
                setState({ regions: allRegions });
            };

            fetchData().finally(() => setFilterState({ loading: false }));
        }
    }, [filterState.filters.length]);

    useEffect(() => {
        if (setLocationPermissionAccessToAll) {
            setLocationPermissionAccessToAll(state.locationPermissionAccessToAll)
        }
    }, [state.locationPermissionAccessToAll])

    useEffect(() => {
        if (linkServiceProviders) {
            const newValue = userRestrictedLocations ? "0" : "1";
            setState({ locationPermissionAccessToAll: newValue });
        }
    }, [userRestrictedLocations])

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    if (loading) {
        return <LoadingSkeleton />;
    }

    return (
        <Grid
            container
            {...gridContainerProps}
            sx={{ width: dynamicWidth, marginTop: '2%', marginLeft: '1%' }}
        >

            <Grid item xs={4} alignSelf="center">
                {(renderRegions && filterState.filterListOptions.length > 0) && (
                    <FilterList
                        tableURL={filterState.tableURL}
                        filters={filterState.filterListOptions}
                        filterValues={filterState.filterValues}
                        appliedFilters={filterState.filters}
                        setAppliedFilters={appliedFilters => setFilterState({ filters: appliedFilters })}
                        selectedFilter={filterState.selectedFilter}
                        setSelectedFilter={selectedFilter => setFilterState({ selectedFilter })}
                        handleClearFilters={handleClearFilters}
                        setClearFilters={clearFilters => setFilterState({ clearFilters })}
                    />
                )}
            </Grid>

            {(canEditLocation && (isViewingAssignedLocations || addingNewRegionalAdmin()) && userStatus !== 'Deleted') && (
                <Grid item sx={{ marginLeft: "auto", marginBottom: "1%" }} align="right">
                    <PrimaryButton
                        buttonText="Assign Locations"
                        onClick={() => navigate.push(`/UserUpdate/${userId}?assignedLocations=1`)}
                        disabled={loading}
                    />
                </Grid>
            )}

            {state.isProviderRestricted && (
                <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 1 }}>
                        Location options are limited to those assigned to the provider account.
                    </Alert>
                </Grid>
            )}

            {(canEditLocation || isCreatingNewUser || linkServiceProviders) && (
                <Grid item xs={4} sx={{ marginLeft: "auto" }} alignSelf="center" align="right">
                    <FormControl disabled={isOptionDisabled || determineRadioSelectionDisabled() || addingNewRegionalAdmin()}>
                        <RadioGroup
                            row
                            aria-labelledby="location-permissions"
                            value={state.locationPermissionAccessToAll}
                            onChange={e => handleAccessToAllChange(e.target.value)}
                            sx={{ opacity: (isOptionDisabled || determineRadioSelectionDisabled() || addingNewRegionalAdmin()) ? "40%" : "100%" }}
                        >
                            <FormControlLabel
                                disableTypography
                                label={state.isProviderRestricted ? "Access to all provider account locations" : "Access to all locations"}
                                className="formControlLabel"
                                value="1"
                                control={
                                    <Radio
                                        size="small"
                                        sx={{
                                            color: trakrefBlue,
                                            '&.Mui-checked': { color: trakrefBlue }
                                        }}
                                    />
                                }
                            />
                            <FormControlLabel
                                disableTypography
                                label="Choose from a list"
                                className="formControlLabel"
                                value="0"
                                control={
                                    <Radio
                                        size="small"
                                        sx={{
                                            color: trakrefBlue,
                                            '&.Mui-checked': { color: trakrefBlue }
                                        }}
                                    />
                                }
                            />
                        </RadioGroup>
                    </FormControl>
                </Grid>
            )}

            {renderRegions && (
                (state.regions.length > 0) ? (
                    state.regions.map((region, index) => {

                        const tooltipText = determineTooltipText(region.id);
                        return (
                            <Grid item xs={12}>
                                <Accordion
                                    key={region.id}
                                    variant='outlined'
                                    disabled={filterState.loading}
                                    expanded={state.expandedRegionId === region.id}
                                    TransitionProps={{ unmountOnExit: true }}
                                >
                                    <AccordionSummary
                                        sx={{ "& .MuiAccordionSummary-content": { margin: "4px 0px", cursor: "auto" } }}
                                    >
                                        <Grid container direction='row' alignItems="center" >
                                            <Grid item xs={8}>
                                                <Typography className="region-label">
                                                    <RegionLabel
                                                        regionName={region.label}
                                                        numberOfLocations={region.numberOfLocations}
                                                    />
                                                </Typography>
                                            </Grid>
                                            <Grid item sx={{ marginLeft: "auto" }}>
                                                <Tooltip title={tooltipText}>
                                                    <span>
                                                        <OnOffSwitch
                                                            value={state.enabledRegionIds.includes(region.id)}
                                                            onChange={() => handleRegionToggle(region.id)}
                                                            disabled={isViewingAssignedLocations || addingNewRegionalAdmin()}
                                                        />
                                                    </span>
                                                </Tooltip>
                                            </Grid>
                                            <Grid item>
                                                <IconButton onClick={() => handleRegionOpen(region.id)}>
                                                    {state.expandedRegionId === region.id ? (
                                                        <ExpandLessIcon />
                                                    ) : (
                                                        <ExpandMoreIcon />
                                                    )}
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    </AccordionSummary>
                                    <AccordionDetails key={index}>
                                        <TextField
                                            label="Search For Locations"
                                            variant="outlined"
                                            value={state.searchTerm}
                                            onChange={e => handleSearch(e.target.value, region.id)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                '& .MuiInputBase-input': { padding: '6.5px 14px' },
                                                '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                                    borderColor: trakrefBlue
                                                }
                                            }}
                                        />
                                        {state.regions[index].locations.length > 0 ? (
                                            <InstanceAssociationsTable
                                                regionId={region.id}
                                                locations={region.locations}
                                                columns={columns}
                                                state={state}
                                                handleLocationCheck={handleLocationCheck}
                                                comingFromUserForm={comingFromUserForm}
                                                linkServiceProviders={linkServiceProviders}
                                            />
                                        ) : (
                                            <Grid item xs={8}>
                                                <NoData usePaperBackground={false} />
                                            </Grid>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>
                        );
                    })
                ) : (
                    <Grid item xs={8}>
                        <NoData />
                    </Grid>
                )
            )}
        </Grid>
    );
};

export default AssignedLocations;
